/** Calendar days, as the server files them: "YYYY-MM-DD" in Asia/Seoul.
 *
 *  metrics_daily dates are AdMob's reporting day (Asia/Seoul), so "today" is
 *  Seoul's today whatever zone the laptop is in. It is read here, once, and
 *  every other day is counted from it as text — `Date.toISOString()` (UTC)
 *  and `new Date('2026-10-21')` (UTC midnight) are never used for a day. */

export const SEOUL = 'Asia/Seoul'
const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']

const seoulDay = new Intl.DateTimeFormat('en-CA', { timeZone: SEOUL, year: 'numeric', month: '2-digit', day: '2-digit' })

/** Seoul's date now: "2026-10-21". */
export function seoulToday(now: Date = new Date()): string {
  return seoulDay.format(now)
}

function parts(date: string): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number)
  return [y, m, d]
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A day shifted by n days, counted on the calendar (no zone involved). */
export function addDays(date: string, n: number): string {
  const [y, m, d] = parts(date)
  const t = new Date(Date.UTC(y, m - 1, d + n))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

/** Days from a to b (b − a). */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = parts(a)
  const [by, bm, bd] = parts(b)
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000)
}

/** "2026-10" of a day. */
export function monthOf(date: string): string {
  return date.slice(0, 7)
}

/** A month shifted by n months: addMonths('2026-01', -1) = '2025-12'. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1 + n, 1))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`
}

/** The last day of a month: "2026-02-28". */
export function monthEnd(month: string): string {
  return addDays(`${addMonths(month, 1)}-01`, -1)
}

/** The weekday of a day, in Korean: "화". */
export function weekday(date: string): string {
  const [y, m, d] = parts(date)
  return WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
}

/** A day as a heading: "10. 21. (화)", with the year when it is not
 *  `today`'s year. */
export function dayTitle(date: string, today: string = seoulToday()): string {
  const [y, m, d] = parts(date)
  const base = `${m}. ${d}. (${weekday(date)})`
  return y === parts(today)[0] ? base : `${y}. ${base}`
}

/** A month as a heading: "2026. 10". */
export function monthTitle(month: string): string {
  const [y, m] = month.split('-')
  return `${y}. ${m}`
}

/** The ?range= vocabulary (plan 1-3). */
export type RangeKey = 'today' | 'yesterday' | '7d' | '30d' | '90d' | `month:${string}`

export const RANGE_LABEL: Record<'today' | 'yesterday' | '7d' | '30d' | '90d', string> = {
  today: '오늘',
  yesterday: '어제',
  '7d': '7일',
  '30d': '30일',
  '90d': '90일',
}

const MONTH_RANGE = /^month:(\d{4})-(0[1-9]|1[0-2])$/

/** A ?range= value, or `fallback` when it is missing or not one of ours. */
export function parseRange(v: string | null | undefined, fallback: RangeKey = '30d'): RangeKey {
  if (!v) return fallback
  if (v in RANGE_LABEL || MONTH_RANGE.test(v)) return v as RangeKey
  return fallback
}

/** The days a range covers, both ends included. "7d" is the last seven days
 *  with today, the way the dashboard's last7 is (today − 6 … today); a month
 *  runs to its end, or to today when it is this month. */
export function rangeToFromTo(range: RangeKey, today: string = seoulToday()): { from: string; to: string } {
  switch (range) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday': {
      const y = addDays(today, -1)
      return { from: y, to: y }
    }
    case '7d':
      return { from: addDays(today, -6), to: today }
    case '30d':
      return { from: addDays(today, -29), to: today }
    case '90d':
      return { from: addDays(today, -89), to: today }
  }
  const month = range.slice('month:'.length)
  const end = monthEnd(month)
  return { from: `${month}-01`, to: end > today ? today : end }
}

/** A range's name on screen: "30일", "2026. 09". */
export function rangeLabel(range: RangeKey): string {
  return range.startsWith('month:') ? monthTitle(range.slice('month:'.length)) : RANGE_LABEL[range as keyof typeof RANGE_LABEL]
}

/** Whether a range's days include today, which AdMob is still revising. */
export function rangeIsPartial(range: RangeKey, today: string = seoulToday()): boolean {
  return rangeToFromTo(range, today).to === today
}

/** The last n months, newest first, ending with today's month. */
export function lastMonths(n: number, today: string = seoulToday()): string[] {
  const m = monthOf(today)
  return Array.from({ length: n }, (_, i) => addMonths(m, -i))
}
